import { DefaultTheme } from 'styled-components';

// Define the breakpoints as a standalone object with explicit types
const breakpointValues = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

const breakpoints = {
  values: breakpointValues,
  up: (key: keyof typeof breakpointValues) => `@media (min-width:${breakpointValues[key]}px)`,
  down: (key: keyof typeof breakpointValues) => `@media (max-width:${breakpointValues[key]}px)`,
};

// Define the structure of your theme, including breakpoints
export interface AppTheme {
  colors: {
    primary: { main: string; light: string; dark: string };
    secondary: { main: string; light: string; dark: string };
    success: { main: string; light: string; dark: string };
    error: { main: string; light: string; dark: string };
    warning: { main: string; light: string; dark: string };
    info: { main: string; light: string; dark: string };
    
    background: {
      primary: string;
      secondary: string;
      paper: string;
      contrast: string;
    };
    
    text: {
      primary: string;
      secondary: string;
      disabled: string;
      contrast: string;
    };
    
    border: string;
    divider: string;
    
    gray: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  };
  
  spacing: {
    (factor: number): string;
    unit: number;
  };
  
  typography: {
    fontFamily: string;
    htmlFontSize: number;
    h1: { fontSize: string; fontWeight: number };
    h2: { fontSize: string; fontWeight: number };
    h3: { fontSize: string; fontWeight: number };
    h4: { fontSize: string; fontWeight: number };
    body1: { fontSize: string; fontWeight: number };
    body2: { fontSize:string; fontWeight: number };
    caption: { fontSize: string; fontWeight: number };
    button: { fontSize: string; fontWeight: number; textTransform: string };
  };
  
  shape: {
    borderRadius: {
      small: string;
      medium: string;
      large: string;
      xlarge: string;
    };
  };

  shadows: {
    small: string;
    medium: string;
    large: string;
  };

  zIndex: {
    appBar: number;
    modal: number;
    tooltip: number;
  };

  breakpoints: typeof breakpoints;
}

// Define your actual theme object matching the AppTheme interface
export const darkTheme: AppTheme = {
  colors: {
    primary: { main: '#00aaff', light: '#67daff', dark: '#007ac1' }, // Blue
    secondary: { main: '#ff9800', light: '#ffc947', dark: '#c66900' }, // Orange
    success: { main: '#4caf50', light: '#81c784', dark: '#388e3c' }, // Green
    error: { main: '#f44336', light: '#e57373', dark: '#d32f2f' }, // Red
    warning: { main: '#ffeb3b', light: '#fff79a', dark: '#c8b900' }, // Yellow
    info: { main: '#2196f3', light: '#6ec6ff', dark: '#0069c0' }, // Light Blue
    
    background: {
      primary: '#121212', // Very dark grey (almost black)
      secondary: '#1e1e1e', // Dark grey
      paper: '#2a2a2a', // Slightly lighter grey for cards/modals
      contrast: '#ffffff',
    },
    
    text: {
      primary: '#e0e0e0', // Light grey for primary text
      secondary: '#a0a0a0', // Medium grey for secondary text
      disabled: '#616161', // Darker grey for disabled text
      contrast: '#121212',
    },
    
    border: '#444',
    divider: 'rgba(255, 255, 255, 0.12)',
    
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  
  spacing: Object.assign(
    (factor: number) => `${factor * 8}px`,
    { unit: 8 }
  ),
  
  typography: {
    fontFamily: "'Inter', sans-serif",
    htmlFontSize: 16,
    h1: { fontSize: '2.5rem', fontWeight: 700 },
    h2: { fontSize: '2rem', fontWeight: 700 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    body1: { fontSize: '1rem', fontWeight: 400 },
    body2: { fontSize: '0.875rem', fontWeight: 400 },
    caption: { fontSize: '0.75rem', fontWeight: 400 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'uppercase' },
  },

  shape: {
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px',
      xlarge: '16px',
    },
  },
  
  shadows: {
    small: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    medium: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
    large: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
  },

  zIndex: {
    appBar: 1100,
    modal: 1300,
    tooltip: 1500,
  },
  breakpoints,
};

export const lightTheme: AppTheme = {
  ...darkTheme, // Start with dark theme and override colors
  colors: {
    ...darkTheme.colors,
    primary: { main: '#007bff', light: '#58a6ff', dark: '#0056b3' },
    background: {
      primary: '#f8f9fa',
      secondary: '#e9ecef',
      paper: '#ffffff',
      contrast: '#121212',
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
      disabled: '#adb5bd',
      contrast: '#ffffff',
    },
    border: '#dee2e6',
    divider: 'rgba(0, 0, 0, 0.12)',
    gray: { // Inverted gray scale for light theme
      50: '#212121',
      100: '#424242',
      200: '#616161',
      300: '#757575',
      400: '#9e9e9e',
      500: '#bdbdbd',
      600: '#e0e0e0',
      700: '#eeeeee',
      800: '#f5f5f5',
      900: '#fafafa',
    },
  },
}; 